export function buildSchemaRegistry(specification) {
  const registry = {};
  if (!specification || typeof specification !== 'object') {
    return registry;
  }

  const registerSchemas = (schemas) => {
    if (!schemas || typeof schemas !== 'object') {
      return;
    }
    Object.entries(schemas).forEach(([name, schema]) => {
      if (name && schema && typeof schema === 'object' && !registry[name]) {
        registry[name] = schema;
      }
    });
  };

  registerSchemas(specification.components?.schemas);

  const valueEntries = specification.properties?.schemas?.value;
  if (Array.isArray(valueEntries)) {
    valueEntries.forEach((entry) => {
      registerSchemas(entry?.document?.components?.schemas);
      registerSchemas(entry?.components?.schemas);
    });
  }

  registerSchemas(specification.properties?.schemas?.components?.schemas);

  return registry;
}

export function deriveBodySchemaFields({
  requestRepresentations = [],
  schemaRegistry = {},
} = {}) {
  if (!Array.isArray(requestRepresentations) || requestRepresentations.length === 0) {
    return [];
  }

  const preference = ['application/json', 'application/*+json', 'text/json'];
  const ordered = [...requestRepresentations].sort((a, b) => {
    const aIdx = preference.indexOf(a?.contentType || '');
    const bIdx = preference.indexOf(b?.contentType || '');
    return (aIdx === -1 ? preference.length : aIdx) - (bIdx === -1 ? preference.length : bIdx);
  });

  for (const representation of ordered) {
    const typeName = representation?.typeName;
    if (!typeName) continue;
    const baseSchema = schemaRegistry[typeName];
    if (!baseSchema) continue;
    const { schema } = resolveSchema(baseSchema, schemaRegistry, new Set());
    const fields = summarizeSchemaProperties(schema, schemaRegistry);
    if (fields.length > 0) {
      return fields;
    }
  }

  return [];
}

function summarizeSchemaProperties(schemaLike, registry) {
  const { schema } = resolveSchema(schemaLike, registry, new Set());
  if (!schema || typeof schema !== 'object') {
    return [];
  }

  const properties = schema.properties;
  if (!properties || typeof properties !== 'object') {
    return [];
  }

  const requiredSet = new Set(Array.isArray(schema.required) ? schema.required : []);

  return Object.entries(properties).map(([name, propSchema]) => {
    const { schema: resolvedProp, refName } = resolveSchema(propSchema, registry, new Set());
    const type = describeSchemaType(propSchema, resolvedProp, registry);
    const description = pickSchemaDescription(propSchema, resolvedProp);
    const constraints = collectSchemaConstraints(resolvedProp || propSchema);
    const enumValues = collectEnumValues(resolvedProp || propSchema, registry);
    const notes = collectSchemaNotes({ propSchema, resolvedProp, refName, registry });
    let itemsType = '';
    if (typeof type === 'string') {
      const match = type.match(/^array<(.+)>$/i);
      if (match && match[1]) {
        itemsType = match[1];
      }
    }

    return {
      name,
      type,
      itemsType,
      description,
      constraints,
      enumValues,
      notes,
      required: requiredSet.has(name),
    };
  });
}

function resolveSchema(schemaLike, registry, lineage = new Set()) {
  if (!schemaLike || typeof schemaLike !== 'object') {
    return { schema: null, refName: null };
  }

  if (schemaLike.$ref && typeof schemaLike.$ref === 'string') {
    const refName = extractRefName(schemaLike.$ref);
    if (!refName || lineage.has(refName)) {
      return { schema: registry?.[refName] || null, refName };
    }
    lineage.add(refName);
    const target = registry?.[refName];
    if (!target) {
      return { schema: null, refName };
    }
    const resolved = resolveSchema(target, registry, lineage);
    return { schema: resolved.schema, refName: resolved.refName || refName };
  }

  if (Array.isArray(schemaLike.allOf)) {
    const merged = schemaLike.allOf.reduce((acc, fragment) => {
      const { schema: resolvedFragment } = resolveSchema(fragment, registry, new Set(lineage));
      return mergeSchemaFragments(acc, resolvedFragment);
    }, { ...schemaLike });
    delete merged.allOf;
    return { schema: merged, refName: null };
  }

  return { schema: schemaLike, refName: null };
}

function mergeSchemaFragments(base, addition) {
  const target = { ...(base || {}) };
  if (!addition || typeof addition !== 'object') {
    return target;
  }

  if (addition.properties && typeof addition.properties === 'object') {
    target.properties = {
      ...(target.properties || {}),
      ...addition.properties,
    };
  }

  if (Array.isArray(addition.required)) {
    const existing = new Set(target.required || []);
    addition.required.forEach((item) => existing.add(item));
    target.required = Array.from(existing);
  }

  const keysToCopy = [
    'type',
    'format',
    'description',
    'items',
    'enum',
    'nullable',
    'default',
    'minLength',
    'maxLength',
    'pattern',
    'minimum',
    'maximum',
    'exclusiveMinimum',
    'exclusiveMaximum',
    'minItems',
    'maxItems',
    'uniqueItems',
  ];
  keysToCopy.forEach((key) => {
    if (addition[key] !== undefined && target[key] === undefined) {
      target[key] = addition[key];
    }
  });

  return target;
}

function describeSchemaType(originalSchema, resolvedSchema, registry) {
  if (originalSchema && typeof originalSchema === 'object' && originalSchema.$ref) {
    const refName = extractRefName(originalSchema.$ref);
    if (refName) {
      return refName;
    }
  }

  const schema = resolvedSchema || originalSchema;
  if (!schema || typeof schema !== 'object') {
    return 'unknown';
  }

  if (schema.type === 'array') {
    const { schema: itemSchema } = resolveSchema(schema.items, registry, new Set());
    const itemType = describeSchemaType(schema.items, itemSchema, registry);
    return `array<${itemType}>`;
  }

  if (schema.type) {
    return schema.type;
  }

  if (schema.enum) {
    return 'enum';
  }

  if (schema.properties) {
    return 'object';
  }

  return 'unknown';
}

function pickSchemaDescription(originalSchema, resolvedSchema) {
  if (originalSchema && typeof originalSchema.description === 'string' && originalSchema.description.trim()) {
    return originalSchema.description.trim();
  }
  if (resolvedSchema && typeof resolvedSchema.description === 'string' && resolvedSchema.description.trim()) {
    return resolvedSchema.description.trim();
  }
  return '';
}

function collectSchemaConstraints(schema) {
  if (!schema || typeof schema !== 'object') {
    return [];
  }

  const constraints = [];
  const pushValue = (label, value) => {
    if (value !== undefined && value !== null) {
      constraints.push(`${label}: ${formatConstraintValue(value)}`);
    }
  };

  pushValue('format', schema.format);
  pushValue('pattern', schema.pattern);
  pushValue('minimum', schema.minimum);
  pushValue('maximum', schema.maximum);
  pushValue('exclusiveMinimum', schema.exclusiveMinimum);
  pushValue('exclusiveMaximum', schema.exclusiveMaximum);
  pushValue('minLength', schema.minLength);
  pushValue('maxLength', schema.maxLength);
  pushValue('minItems', schema.minItems);
  pushValue('maxItems', schema.maxItems);
  pushValue('multipleOf', schema.multipleOf);
  pushValue('default', schema.default);

  if (schema.nullable) {
    constraints.push('nullable');
  }
  if (schema.uniqueItems) {
    constraints.push('uniqueItems');
  }

  return constraints;
}

function collectSchemaNotes({ propSchema, resolvedProp, refName, registry }) {
  const notes = [];

  if (propSchema && typeof propSchema === 'object' && propSchema.$ref) {
    const referenced = extractRefName(propSchema.$ref);
    if (referenced) {
      notes.push(`Schema reference: ${referenced}`);
    }
  } else if (refName) {
    notes.push(`Schema reference: ${refName}`);
  }

  const schema = resolvedProp || propSchema;
  if (schema && typeof schema === 'object' && schema.type === 'array') {
    const itemRef = schema.items && typeof schema.items === 'object' && schema.items.$ref
      ? extractRefName(schema.items.$ref)
      : null;
    if (itemRef) {
      notes.push(`Array items schema: ${itemRef}`);
    } else {
      const { schema: itemSchema } = resolveSchema(schema.items, registry, new Set());
      if (itemSchema && itemSchema.properties) {
        notes.push('Array items have nested object properties.');
      }
    }
  }

  if (schema && schema.additionalProperties) {
    notes.push('Allows additional properties.');
  }

  return notes;
}

function collectEnumValues(schemaLike, registry) {
  const values = [];
  const seen = new Set();

  const addValue = (value) => {
    const formatted = formatConstraintValue(value);
    if (formatted && !seen.has(formatted)) {
      seen.add(formatted);
      values.push(formatted);
    }
  };

  const visit = (node, lineage = new Set()) => {
    const { schema } = resolveSchema(node, registry, new Set(lineage));
    if (!schema || typeof schema !== 'object') {
      return;
    }

    if (Array.isArray(schema.enum)) {
      schema.enum.forEach(addValue);
    }

    if (schema.type === 'array' && schema.items) {
      visit(schema.items, lineage);
    }

    if (Array.isArray(schema.oneOf)) {
      schema.oneOf.forEach((option) => visit(option, lineage));
    }
    if (Array.isArray(schema.anyOf)) {
      schema.anyOf.forEach((option) => visit(option, lineage));
    }
    if (Array.isArray(schema.allOf)) {
      schema.allOf.forEach((option) => visit(option, lineage));
    }
  };

  visit(schemaLike);
  return values;
}

function extractRefName(ref) {
  if (!ref || typeof ref !== 'string') {
    return '';
  }
  const segments = ref.split('/');
  return segments[segments.length - 1] || '';
}

function formatConstraintValue(value) {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
}
